import { resolveImageSrc } from "@/shared/ui/AppImage";
import { config } from "@/shared/lib/config";
import { uploadApi } from "@/shared/lib/upload-api";
import { uploadFileToPresignedUrl } from "@/shared/lib/upload-to-presigned";
import { Button, Label, toast } from "@devhop/ui";
import { ImageIcon, X } from "lucide-react";
import {
  useRef,
  useState,
  useImperativeHandle,
  forwardRef,
  useEffect,
} from "react";

const ACCEPT = "image/*";
const KEY_PREFIX = "uploads/bill-logos";
const MAX_SIZE_MB = 2;

/**
 * ดึง key จาก URL ของเรา (config.filesUrl(key))
 * คืน null ถ้าไม่ใช่ URL จาก object store ของเรา
 */
export function extractKeyFromFilesUrl(url: string | null | undefined): string | null {
  if (!url || typeof url !== "string" || !url.trim()) return null;
  const match = url.match(/\/api\/files\/(.+)$/);
  const keyPart = match?.[1];
  if (!keyPart) return null;
  try {
    return decodeURIComponent(keyPart);
  } catch {
    return keyPart;
  }
}

export type BillLogoUploadSubmitValue = {
  logoUrl: string | null;
  oldKeyToDelete?: string | null;
};

export type BillLogoUploadFieldRef = {
  getSubmitValue: (branchId: string) => Promise<BillLogoUploadSubmitValue>;
  resetPending: () => void;
};

type BillLogoUploadFieldProps = {
  value: string | null;
  branchId: string | null;
  disabled?: boolean;
};

export const BillLogoUploadField = forwardRef<
  BillLogoUploadFieldRef,
  BillLogoUploadFieldProps
>(function BillLogoUploadField({ value, disabled }, ref) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [clearIntent, setClearIntent] = useState(false);

  const [objectUrl, setObjectUrl] = useState<string | null>(null);

  useEffect(() => {
    if (pendingFile) {
      const url = URL.createObjectURL(pendingFile);
      setObjectUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    setObjectUrl(null);
  }, [pendingFile]);

  const previewSrc = pendingFile ? objectUrl : clearIntent ? null : value;
  const resolvedPreview = previewSrc
    ? previewSrc.startsWith("blob:")
      ? previewSrc
      : resolveImageSrc(previewSrc)
    : null;

  useImperativeHandle(
    ref,
    () => ({
      async getSubmitValue(bId: string): Promise<BillLogoUploadSubmitValue> {
        const oldKey =
          value && !clearIntent && !pendingFile
            ? null
            : extractKeyFromFilesUrl(value);

        if (pendingFile) {
          const ext = pendingFile.name.includes(".")
            ? `.${pendingFile.name.split(".").pop() ?? ""}`
            : ".png";
          const key = `${KEY_PREFIX}/${bId}/${Date.now()}${ext}`;
          const contentType = pendingFile.type || "image/png";

          const { uploadUrl } = await uploadApi.getPresignUrl({
            key,
            contentType,
            expiresIn: 900,
          });
          await uploadFileToPresignedUrl(uploadUrl, pendingFile, {
            contentType,
          });

          return {
            logoUrl: config.filesUrl(key),
            oldKeyToDelete: oldKey,
          };
        }

        if (clearIntent) {
          return {
            logoUrl: null,
            oldKeyToDelete: oldKey,
          };
        }

        return { logoUrl: value ?? null };
      },
      resetPending() {
        setPendingFile(null);
        setClearIntent(false);
        if (inputRef.current) inputRef.current.value = "";
      },
    }),
    [value, pendingFile, clearIntent],
  );

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      toast.error(`ຂະໜາດໄຟລ໌ເກີນ ${MAX_SIZE_MB}MB`);
      return;
    }
    if (!file.type.startsWith("image/")) {
      toast.error("ກະລຸນາເລືອກໄຟລ໌ຮູບພາບເທົ່ານັ້ນ");
      return;
    }
    setPendingFile(file);
    setClearIntent(false);
  };

  const handleClear = () => {
    setPendingFile(null);
    setClearIntent(true);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleSelect = () => {
    if (disabled) return;
    inputRef.current?.click();
  };

  return (
    <div className="space-y-2">
      <Label>Logo</Label>
      <div className="flex flex-wrap items-start gap-4">
        <div className="flex size-24 shrink-0 items-center justify-center overflow-hidden rounded border bg-muted">
          {resolvedPreview ? (
            <img
              src={resolvedPreview}
              alt="Logo preview"
              className="size-full object-contain"
            />
          ) : (
            <ImageIcon className="h-10 w-10 text-muted-foreground" />
          )}
        </div>
        <div className="flex flex-col gap-2">
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            className="hidden"
            onChange={handleFileChange}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleSelect}
            disabled={disabled}
          >
            ເລືອກຮູບ
          </Button>
          {(resolvedPreview || pendingFile) && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleClear}
              disabled={disabled}
              className="text-muted-foreground hover:text-foreground"
            >
              <X className="mr-1 h-4 w-4" />
              ລຶບ
            </Button>
          )}
        </div>
      </div>
      <p className="text-muted-foreground text-xs">
        ເລືອກຮູບ logo ທີ່ຕ້ອງການສະແດງໃນຫົວບິນ (ສູງສຸດ {MAX_SIZE_MB}MB). ຈະອັບໂຫຼດເມື່ອກົດບັນທຶກ.
      </p>
    </div>
  );
});
